import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import React, { useCallback, useMemo, useState } from 'react';
import SubscriptionPlanType, {
  getSubscriptionPlanLabel,
} from 'crono-fe-common/types/enums/subscriptionPlanType';
import { FeConstants, planFeatures } from 'constants/FeConstants';
import { mapSubscriptionToPlanType, sortPlans } from 'utils/businessLogic';
import { Options, Plan } from 'pages/signup/types';
import useGetSubscriptionProducts from 'hooks/services/subscription/useGetSubscriptionProducts';
import PlanShortCard from 'pages/signup/planSteps/planType/planShortCard';
import { CircularProgress, Typography } from '@mui/material';
import FeatureListItem from 'pages/signup/planSteps/planType/featureListItem';
import BillingSwitch from 'pages/signup/planSteps/planType/billingSwitch';
import { BillingPlan } from 'pages/signup/model';
import { MainButton } from 'crono-fe-common/components/CronoButton';
import UpgradeDeniedModal from './upgradeDenied';
import { UpgradeSubscriptionModalWrapper } from './style';
import CloseTabButton from 'crono-fe-common/components/CronoButton/closeTabButton';
import { useAuth } from 'context/auth';
import Role from 'crono-fe-common/types/enums/role';
import useCheckoutSession from 'hooks/services/subscription/useCheckoutSession';
import useGetSubscription from 'hooks/services/subscription/useGetSubscription';
import { useAnalytics } from 'context/analytics';
import { useUpgradeModal } from 'context/upgradeModal';
import { Constants } from 'crono-fe-common/constants/constants';
import { cookieDomain, cookies } from 'constants/crono-cookie';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import usePortalSession from 'hooks/services/subscription/usePortalSession';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError } from 'crono-fe-common/utils';
import useCheckPaymentCustomer from 'hooks/services/subscription/useCheckPaymentCustomer';
import SelectBillingCountryModal from 'pages/subscriptionPlans/selectBillingCountryModal';
import { StripeEnvironment } from 'crono-fe-common/types/otherSettings';

const UpgradeSubscriptionModal = () => {
  const analytics = useAnalytics();
  const { user } = useAuth();
  const { closeModal, isUpgradeModalVisible, title, subtitle } =
    useUpgradeModal();

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  //By default we use the UK ones to be shown, the correct pick of the one wanted by the user is going to be done later
  const {
    data: subscriptionProducts,
    isLoading: isSubscriptionProductsLoading,
  } = useGetSubscriptionProducts('Uk');

  const options: Options[] = useMemo(() => {
    let allowedPlans = (subscriptionProducts?.data?.data ?? []).filter(
      (s) =>
        s.subscriptionType !== SubscriptionPlanType.FREE_TRIAL &&
        s.subscriptionType !== SubscriptionPlanType.STARTER,
    );

    if (user?.subscriptionType === SubscriptionPlanType.PRO) {
      allowedPlans = allowedPlans.filter(
        (s) => s.subscriptionType !== SubscriptionPlanType.PRO,
      );
    }

    const plansOptions = allowedPlans.map((plan) => {
      return {
        ...plan,
        features:
          planFeatures[plan.subscriptionType as keyof typeof planFeatures],
      };
    });

    const sorted = sortPlans(plansOptions);
    sorted.push({
      id: null,
      subscriptionType: SubscriptionPlanType.Custom,
      features: planFeatures['Custom'],
      prices: [],
      description: null,
    });

    return sorted;
  }, [subscriptionProducts, user]);

  const [billingPlan, setBillingPlan] = useState<BillingPlan>('yearly');

  const [planSelectedIndex, setPlanSelectedIndex] = React.useState<number>(0);

  const selectedPlan = useMemo(
    () => options[planSelectedIndex],
    [options, planSelectedIndex],
  );

  const [isUpgradeDenied, setIsUpgradeDenied] = useState(false);

  const {
    mutateAsync: callCheckout,
    isLoading: callingCheckout,
    error: errorCheckout,
  } = useCheckoutSession();

  const {
    mutateAsync: callPortal,
    isLoading: callingPortal,
    error: errorCallingPortal,
  } = usePortalSession();

  const { data: subscriptionDataDb } = useGetSubscription();
  const subscriptionData = subscriptionDataDb?.data.data ?? null;
  const activeLicenses = subscriptionData?.activeLicenses ?? 0;

  const { data: paymentCustomer } = useCheckPaymentCustomer(
    isUpgradeModalVisible,
  );
  const isStripeCustomer = Boolean(paymentCustomer?.data.data);

  const [showPickBillingCountry, setShowPickBillingCountry] =
    useState<boolean>(false);

  const handleNewCheckout = async () => {
    //For stripe customers we open the customer portal, for non-stripe customers we open the checkout session
    if (isStripeCustomer) {
      try {
        const res = await callPortal({});
        if (res.data.data.url) {
          window.open(res.data.data.url, '_self');
        }
      } catch (e) {
        console.error('Checkout portal error:', e);
      }

      return;
    }
    setShowPickBillingCountry(true);
  };

  const handleCheckout = (
    priceId: string,
    stripeEnvironment: StripeEnvironment,
  ) => {
    if (window.gr) {
      window.gr('track', 'conversion', {
        email: user?.email,
        uid: user?.id?.toString(),
      });
    }

    callCheckout({
      priceId,
      quantity: activeLicenses,
      stripeEnvironment,
    })
      .then((res) => {
        const url = res.data?.data.url;
        if (url) {
          if (analytics) {
            const plan = options.find((plan) => {
              const price = plan?.prices.find((price) => price?.id === priceId);
              return price;
            });
            //Set the thank-you cookie to show the thank you page after the payment
            cookies.set(FeConstants.cronoThankYouCookie, '1', {
              domain: cookieDomain,
              path: '/',
              //Expires in 20 minutes
              expires: new Date(Date.now() + 20 * 60 * 1000),
              sameSite: 'none',
              secure: true,
            });

            analytics.track('user-checkout-created', {
              priceId,
              billingPlan,
              quantity: activeLicenses,
              cronoPlan: getSubscriptionPlanLabel(plan?.subscriptionType),
            });
          }
          window.open(url, '_self');
        }
      })
      .catch(() => {});
  };

  useConditionalSnackBar([
    {
      condition: !!errorCallingPortal,
      message: getError(errorCallingPortal) ?? 'An error occurred',
      severity: 'error',
    },
    {
      condition: !!errorCheckout,
      message: getError(errorCheckout) ?? 'An error occurred',
      severity: 'error',
    },
  ]);

  if (!isUpgradeModalVisible) return null;

  return (
    <>
      {showPickBillingCountry ? (
        <SelectBillingCountryModal
          billingPlan={billingPlan}
          close={() => setShowPickBillingCountry(false)}
          onSelect={(priceId: string, stripeEnvironment: StripeEnvironment) => {
            handleCheckout(priceId, stripeEnvironment);
          }}
          plan={selectedPlan.subscriptionType ?? SubscriptionPlanType.PRO}
          isLoading={callingCheckout}
        />
      ) : (
        <BlurBackgroundDivFixed
          onClick={handleClose}
          style={{ zIndex: Constants.beforeHighestZIndex }}
        >
          {isUpgradeDenied ? (
            <UpgradeDeniedModal onClose={() => setIsUpgradeDenied(false)} />
          ) : (
            <UpgradeSubscriptionModalWrapper
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cross">
                <CloseTabButton
                  style={{ paddingRight: '24px' }}
                  close={handleClose}
                />
              </div>
              {isSubscriptionProductsLoading ? (
                <FlexDiv>
                  <CircularProgress />
                </FlexDiv>
              ) : (
                <div className={'container'}>
                  <div className={'info'}>
                    <div className={'header'}>
                      <Typography
                        fontSize={24}
                        fontWeight={700}
                        lineHeight={'30px'}
                      >
                        {title}
                      </Typography>
                      <Typography fontSize={16} fontWeight={400}>
                        {subtitle}
                      </Typography>
                    </div>
                    <div className={'features-list'}>
                      {selectedPlan?.features.map((feature, i) => {
                        const type = selectedPlan.subscriptionType;

                        const firstFeature =
                          i === 0 &&
                          type !== SubscriptionPlanType.FREE_TRIAL &&
                          type !== SubscriptionPlanType.STARTER;

                        return (
                          <FeatureListItem
                            key={i}
                            feature={feature}
                            firstFeature={firstFeature}
                            type={type}
                          />
                        );
                      })}
                    </div>
                    <div className={'upgrade-panel'}>
                      <BillingSwitch
                        options={options}
                        selectedPlan={
                          mapSubscriptionToPlanType(
                            selectedPlan?.subscriptionType,
                          ) ?? Plan.PRO
                        }
                        billingPlan={billingPlan}
                        setBillingPlan={setBillingPlan}
                        badgePlacement={'top'}
                      />
                      {selectedPlan.subscriptionType ===
                      SubscriptionPlanType.Custom ? (
                        <MainButton
                          style={{ margin: 0 }}
                          onClick={async () => {
                            window.open(
                              Constants.cronoCustomPlanPage,
                              '_blank',
                            );
                            if (analytics) {
                              await analytics.track(
                                'app-talk-to-sales-clicked',
                                {},
                              );
                            }
                          }}
                        >
                          Talk to sales
                        </MainButton>
                      ) : (
                        <MainButton
                          style={{ margin: 0 }}
                          onClick={() => {
                            if (analytics) {
                              analytics.track(
                                'subscription-popup-upgrade-clicked',
                                {},
                              );
                            }
                            if (
                              user &&
                              user.userRoles?.includes(
                                Role.SUBSCRIPTION_MANAGER,
                              )
                            ) {
                              const priceId = selectedPlan?.prices.filter(
                                (price) => price.paymentCycle === billingPlan,
                              )[0]?.id;

                              if (
                                priceId &&
                                !callingCheckout &&
                                !callingPortal
                              ) {
                                handleNewCheckout();
                              }
                            } else {
                              setIsUpgradeDenied(true);
                            }
                          }}
                        >
                          Upgrade €
                          {
                            selectedPlan?.prices?.find(
                              (p) => p.paymentCycle === billingPlan,
                            )?.monthlyTotalCycle
                          }
                        </MainButton>
                      )}
                    </div>
                  </div>
                  <div className={'upgrade-options'} style={{ minWidth: 290 }}>
                    {options.map((option, index) => {
                      const type = option?.subscriptionType;

                      let price;
                      if (type !== SubscriptionPlanType.Custom) {
                        price = option?.prices.filter(
                          (price) => price.paymentCycle === billingPlan,
                        )[0];
                      }

                      return (
                        <PlanShortCard
                          selected={planSelectedIndex === index}
                          showWhiteBackground={true}
                          type={type}
                          price={price}
                          billingPlan={billingPlan}
                          key={index}
                          onClick={() => setPlanSelectedIndex(index)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </UpgradeSubscriptionModalWrapper>
          )}
        </BlurBackgroundDivFixed>
      )}
    </>
  );
};

export default UpgradeSubscriptionModal;
